import ErrorBoundary from '@/app/error-boundary';
import FpjsTracker from '@/components/fingerprint/fpjs-tracker';
import { Header } from '@/components/header';
import { MIGRATION_ENABLED, MIGRATION_MOCK_ENABLED } from '@/config/migration';
import NotificationsDialog from '@/features/account/components/notifications-dialog';
import { useHashScroll } from '@/hooks';
import { useIsMigrated } from '@/hooks/use-migration';
import IncidentCompensationModal from '@/incidents/april-15/compensation-modal';
import { cn } from '@/utils/cn';
import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import AccountHandler from '../handlers/account-handler';
import ApiHandler from '../handlers/api-handler';
import DiscountGroupsHandler from '../handlers/discount-groups-handler';
import EventHandler from '../handlers/event-handler';
import MigrationHandler from '../handlers/migration-handler';
import AnnouncementBanner from '../header/announcement-banner';
import FundedAccountBanner from '../header/funded-account-banner';
import MaintenanceBanner from '../header/maintenance-banner';
import MigrationBanner from '../header/migration-banner';
import CapitalResultModal from '../header/modals/capital-result-modal';
import CapitalTourModal from '../header/modals/capital-tour-modal';
import DiscountsModal from '../header/modals/discounts-modal';
import MigrationWelcomeModal from '../header/modals/migration-welcome-modal';
import RealtimeConnectionLostBanner from '../header/realtime-connection-lost-banner';
import TrendingBanner from '../header/trending-banner';
import ListingsToast from '../listings-toast';
import MigratedGuard from '../route-guards/migrated-guard';

const MigrationMockPanel = MIGRATION_MOCK_ENABLED
  ? lazy(() => import('../dev/migration-mock-panel'))
  : null;

export default function RootLayout() {
  useHashScroll();
  const isMigrated = useIsMigrated();

  return (
    <ErrorBoundary>
      <ApiHandler />
      <EventHandler />
      <AccountHandler />
      <DiscountGroupsHandler />
      {MIGRATION_ENABLED && <MigrationHandler />}
      <FpjsTracker />
      {!isMigrated && <CapitalResultModal />}
      {!isMigrated && <DiscountsModal />}
      {!isMigrated && <CapitalTourModal />}
      {MIGRATION_ENABLED && <MigrationWelcomeModal />}
      {!isMigrated && <ListingsToast />}
      <div className="flex h-full flex-col">
        <MigrationBanner />
        <AnnouncementBanner />
        <MaintenanceBanner />
        <RealtimeConnectionLostBanner />
        {!isMigrated && <FundedAccountBanner />}
        <Header />
        {!isMigrated && <TrendingBanner />}
        <main className={cn('min-h-0', isMigrated ? 'flex-1' : 'h-full')}>
          <MigratedGuard>
            <Outlet />
          </MigratedGuard>
        </main>
      </div>
      <NotificationsDialog />
      <IncidentCompensationModal />
      {MigrationMockPanel && (
        <Suspense fallback={null}>
          <MigrationMockPanel />
        </Suspense>
      )}
    </ErrorBoundary>
  );
}
